"use client";
import { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';

export default function ForgotPasswordForm() {
  const [forgotStatus, setForgotStatus] = useState('');
  const [forgotResetLink, setForgotResetLink] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotStatus('');
    setForgotResetLink(null);
    setForgotLoading(true);
    const res = await fetch('/api/auth/forgot', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: forgotEmail })
    });
    const data = await res.json().catch(() => ({}));
    setForgotLoading(false);
    if (res.ok) {
      if (data.resetLink) {
        setForgotResetLink(data.resetLink);
        setForgotStatus('Email is not configured here. Use the link below to reset (valid 30 min):');
      } else {
        setForgotStatus('If that email is registered, check your inbox for a password reset link.');
      }
    } else {
      const msg = data.error || 'There was a problem sending reset email.';
      setForgotStatus(data.debug ? `${msg} (${data.debug})` : msg);
    }
  };

  return (
    <Box component="form" onSubmit={handleForgot} sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: { xs: '100%', sm: 300 } }}>
      <Typography variant="h5" component="h2" fontWeight={700} mb={1} color="text.primary">
        Forgot Password
      </Typography>
      <TextField
        label="Your email"
        type="email"
        fullWidth
        required
        value={forgotEmail}
        onChange={(e) => setForgotEmail(e.target.value)}
        variant="outlined"
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={forgotLoading}
        startIcon={forgotLoading ? <CircularProgress size={20} color="inherit" /> : null}
        sx={{
          mt: 1,
          fontWeight: 600,
          py: 1.25,
          backgroundColor: '#2563EB !important',
          color: '#fff !important',
          '&:hover': { backgroundColor: '#1d4ed8 !important', color: '#fff !important' },
          '& .MuiButton-startIcon, & .MuiButton-startIcon svg': { color: '#fff !important', fill: '#fff !important' },
        }}
      >
        {forgotLoading ? 'Sending...' : 'Send reset link'}
      </Button>
      {forgotStatus && (
        <Typography
          variant="body2"
          sx={{ fontSize: 13, mt: 1, color: forgotStatus.includes('problem') ? 'error.main' : 'success.main' }}
        >
          {forgotStatus}
        </Typography>
      )}
      {forgotResetLink && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <Box component="a" href={forgotResetLink} target="_blank" rel="noopener noreferrer" sx={{ wordBreak: 'break-all', color: 'primary.main' }}>
            {forgotResetLink}
          </Box>
        </Typography>
      )}
    </Box>
  );
}
